import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnInit,
	OnChanges,
	Output,
	SimpleChanges,
	AfterViewInit,
	OnDestroy,
	SimpleChange
} from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SelectComponent } from '@shared/components/select/select.component';
import { DropDownItem } from '@shared/models/drop-down-item';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { filter, pairwise, startWith, takeUntil, tap } from 'rxjs/operators';
import { merge, Observable } from 'rxjs';
import { MaterialOption } from '@shared/models/material-option';
import { TextInputComponent } from '@shared/components/text-input/text-input.component';
import { FavoriteSelectorComponent } from '@shared/components/favorite-selector/favorite-selector.component';
import { IdName } from '@shared/models/id-name';
import { Specification } from '@shared/models/specification';
import { ShadeSystem } from '@shared/models/shade-system';
import { PopupIconNames } from '@shared/models/enums/popup-icon.enum';
import { PopUpActions } from '@shared/models/enums/popup-modal-actions.enum';
import { PopupService } from '@shared/services/popup.service';
import { FavoritesEnum } from '@shared/models/enums/favorites.enum';
import { RxIntuitiveCrownFacade } from '../rx-intuitive-crown.facade';
import { Material } from '@shared/models/material';
import { Maybe } from '@/types/globals';
import { SpecificationEnum } from '@core/procedure-helpers/models/specification.enum';
import { IntuitiveIndication } from '@modules/rx-intuitive-teeth-orchestrator/models';
import { CancelEvent } from '@shared/utils/selectors-utils';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';
import { CrownControlName, CrownSelectionContext } from '../models/crown-models';

@Component({
	selector: 'rx-intuitive-crown',
	templateUrl: './rx-intuitive-crown.component.html',
	styleUrls: ['./rx-intuitive-crown.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'[class.rx-intuitive-crown--read-only]': 'isReadOnly'
	},
	imports: [
		AsyncPipe,
		NgIf,
		ReactiveFormsModule,
		TranslateModule,
		SelectComponent,
		TextInputComponent,
		FavoriteSelectorComponent
	],
	providers: [RxIntuitiveCrownFacade]
})
export class RxIntuitiveCrownComponent extends BaseDestroyableComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	@Input() crownForm: CrownForm;
	@Input() indicationId: IntuitiveIndication;
	@Input() customLabMaterialGroup: string | undefined;
	@Input() isReadOnly: boolean;
	@Input() isBridge: boolean;
	@Output() crownFormChanged = new EventEmitter<CrownForm>();

	@Output() selectionContext = new EventEmitter<CrownSelectionContext>();

	selectedMaterial: Maybe<MaterialOption>;
	isOtherShadeSystemSelected: boolean;

	materialOptions: Material[];
	shadeSystemOptions: ShadeSystem[];
	specificationOptions: Specification[];
	incisalBodyGingivalOptions: IdName<string>[];

	favoriteMaterial = FavoritesEnum.Materials;

	readonly form: FormGroup = this.formBuilder.group({
		specification: [null satisfies DropDownItem | null],
		material: [null satisfies DropDownItem | null],
		shadeSystem: [null satisfies DropDownItem | null],
		body: [null satisfies DropDownItem | string | null]
	});

	controlNames = Object.keys(this.form.controls) as CrownControlName[];

	constructor(
		private readonly formBuilder: FormBuilder,
		private readonly facade: RxIntuitiveCrownFacade,
		private readonly popupService: PopupService,
		private readonly cdr: ChangeDetectorRef
	) {
		super();
	}

	get specificationControl() {
		return this.form.controls.specification;
	}

	get materialControl() {
		return this.form.controls.material;
	}

	get shadeSystemControl() {
		return this.form.controls.shadeSystem;
	}

	get bodyControlAsIdName() {
		return this.bodyControl as FormControl<IdName<number>>;
	}

	get bodyControlAsAString() {
		return this.bodyControl as FormControl<string>;
	}

	get bodyControl() {
		return this.form.controls.body;
	}

	get materialFavoriteDisabled(): boolean {
		const isMaterialDisabled = (this.specificationOptions?.length && !this.specificationControl.value) || !this.materialOptions?.length;

		return isMaterialDisabled || this.isReadOnly;
	}

	get shadeControlsDisabled(): boolean {
		return !this.selectedMaterial?.ShadeVisible || this.isReadOnly;
	}

	get specificationSelectCancellable(): boolean {
		return !this.shouldValidateForSend && !this.isReadOnly;
	}

	get bodyShadeRequired(): boolean {
		return !!(this.selectedMaterial?.BodyShadeMandatory && this.shouldValidateForSend);
	}

	get bodyShadeCancellable(): boolean {
		return !this.shouldValidateForSend || !this.selectedMaterial?.BodyShadeMandatory;
	}

	isNotDoctor$: Observable<boolean> = this.facade.isNotDoctor$;
	shouldValidateForSend$: Observable<boolean> = this.facade.shouldValidateForSend$;

	/** Mirrors {@link shouldValidateForSend$} for {@link bodyShadeRequired} / {@link bodyShadeCancellable}. */
	private shouldValidateForSend = false;

	private isProgrammaticUpdate = false;
	private isPopUpEnabled = true;

	ngOnInit(): void {
		this.shouldValidateForSend$.pipe(takeUntil(this.componentAlive$)).subscribe(value => {
			this.shouldValidateForSend = value;
			this.cdr.markForCheck();
		});

		this.subscribeToShadeSystemChanges();
		this.subscribeToSpecificationChanges();
		this.subscribeToMaterialChanges();
		this.subscribeToFormChanges();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes.crownForm) {
			return;
		}
		if (this.hasCrownFormChanged(changes.crownForm) || this.hasIndicationChanged(changes.indicationId)) {
			this.applySelectionState();
		}
	}

	ngAfterViewInit() {
		this.applySelectionState();
		this.cdr.detectChanges();
	}

	handleCancelSpecification() {
		this.form.patchValue({
			specification: null,
			material: null,
			body: null
		});
	}

	handleCancelMaterial(cancelEvent: CancelEvent<DropDownItem>) {
		if (this.bodyControl.value !== null) {
			this.bodyControl.patchValue(null);
		}
		cancelEvent.selector.handleCancelDefault(cancelEvent.event);
	}

	private subscribeToFormChanges() {
		this.form.valueChanges
			.pipe(
				filter(() => !this.isProgrammaticUpdate && !this.isReadOnly),
				tap(() => {
					this.crownFormChanged.emit(this.getCrownFormData());
				}),
				takeUntil(merge(this.componentAlive$))
			)
			.subscribe();
	}

	private applySelectionState() {
		if (!this.crownForm) {
			return;
		}

		const state = this.facade.buildSelectionState(this.crownForm, this.indicationId, this.customLabMaterialGroup, this.isBridge);

		// to prevent triggering valueChanges subscriptions on form validation event
		this.isProgrammaticUpdate = true;

		this.specificationOptions = state.specification.options;
		this.materialOptions = state.material.options;
		this.shadeSystemOptions = state.shadeSystem.options;
		this.incisalBodyGingivalOptions = state.incisalBodyGingivalOptions;
		this.selectedMaterial = state.materialOption;
		this.isOtherShadeSystemSelected = state.shadeSystem.isOtherShadeSystemSelected;

		const bodySelection = this.facade.resolveShadeBodySelection(
			this.crownForm,
			state.shadeSystem.isOtherShadeSystemSelected,
			state.incisalBodyGingivalOptions
		);

		this.form.patchValue(
			{
				specification: state.specification.current,
				material: state.material.current,
				shadeSystem: state.shadeSystem.current,
				body: bodySelection ?? null
			},
			{ emitEvent: false }
		);

		void Promise.resolve().then(() => (this.isProgrammaticUpdate = false));

		this.emitSelectionContext();
	}

	private emitSelectionContext(): void {
		this.selectionContext.emit({
			materialId: this.materialControl.value?.Id ?? null,
			shadeSystemId: this.shadeSystemControl.value?.Id ?? null,
			isOtherShadeSystem: this.isOtherShadeSystemSelected,
			materialOption: this.selectedMaterial ?? undefined
		});
	}

	private subscribeToShadeSystemChanges() {
		this.shadeSystemControl.valueChanges
			.pipe(
				startWith(this.shadeSystemControl.value),
				pairwise(),
				tap(([prevShadeControlValue, nextShadeControlValue]: [IdName<number>, IdName<number>]) => {
					if (nextShadeControlValue) {
						if (this.bodyControl.value) {
							this.openConfirmationPopUp(prevShadeControlValue);
						} else {
							this.updateShadeValuesAndSetOptions();
						}
					}
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private updateShadeValuesAndSetOptions(): void {
		const shadeId = this.shadeSystemControl?.value?.Id;

		this.incisalBodyGingivalOptions = this.facade.resolveIncisalBodyGingivalOptions(this.materialControl?.value?.Id, shadeId);

		this.isOtherShadeSystemSelected = shadeId === -1;
		this.bodyControl.patchValue(null, { emitEvent: false });
		this.crownFormChanged.emit(this.getCrownFormData());
		this.emitSelectionContext();
	}

	private subscribeToSpecificationChanges() {
		this.specificationControl.valueChanges
			.pipe(
				tap(() => {
					this.updateMaterial();
					this.updateMaterialControl();
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private updateMaterial() {
		this.selectedMaterial = this.facade.resolveMaterialOption(
			this.materialControl.value?.Id,
			this.crownForm.specification ?? SpecificationEnum.None,
			this.customLabMaterialGroup
		);
		this.updateShadeSettingsControls();
		this.emitSelectionContext();
	}

	private updateMaterialControl() {
		this.materialOptions = this.facade.getMaterialsBySpecification(
			this.specificationControl.value?.Id,
			this.indicationId,
			this.isBridge
		);
		this.materialControl.patchValue(null, { emitEvent: false });
	}

	private updateShadeSettingsControls() {
		if (!this.selectedMaterial) {
			return;
		}

		if (!this.selectedMaterial?.ShadeVisible) {
			this.bodyControl.patchValue(null, { emitEvent: false });
		}
	}

	private subscribeToMaterialChanges() {
		this.materialControl.valueChanges
			.pipe(
				tap(() => {
					this.updateMaterial();
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private hasCrownFormChanged(crownFormChange: SimpleChange) {
		const current: CrownForm | undefined = crownFormChange?.currentValue;
		const previous: CrownForm | undefined = crownFormChange?.previousValue;

		const currentIds = current?.teethIds ?? [];
		const previousIds = previous?.teethIds ?? [];

		if (currentIds.length !== previousIds.length) {
			return true;
		}

		if (currentIds.some((id, index) => id !== previousIds[index])) {
			return true;
		}

		const keys = (Object.keys({ ...current, ...previous }) as (keyof CrownForm)[]).filter(k => k !== 'teethIds');

		return keys.some(key => current?.[key] !== previous?.[key]);
	}

	private hasIndicationChanged(indicationChange: SimpleChange) {
		return indicationChange?.currentValue !== indicationChange?.previousValue;
	}

	private openConfirmationPopUp(prevShadeControlValue: IdName<number>) {
		if (!this.isPopUpEnabled) {
			return;
		}

		const popUpInput = {
			titleTranslationKey: 'Popup.Confirmation',
			contentTranslationKey: 'ToothEditor.ShadeSystemWarning',
			iconName: PopupIconNames.Question
		};

		this.popupService
			.openConfirmationPopUp({ popUpInput })
			.pipe(takeUntil(this.componentAlive$))
			.subscribe(result => {
				switch (result) {
					case PopUpActions.Cancel:
						this.isPopUpEnabled = false;
						this.shadeSystemControl.patchValue(prevShadeControlValue);
						this.isPopUpEnabled = true;
						break;
					case PopUpActions.Ok:
						this.updateShadeValuesAndSetOptions();
						break;
				}
			});
	}

	private getCrownFormData(): CrownForm {
		return this.facade.mapFormToCrownData(this.form.getRawValue(), this.crownForm.teethIds);
	}
}
